/**
 *
 * "Main module to generate deck canvas for mobile in 2D.
 * Only required for layout and Maintenance."
 *
 * @file   mobileDeckCanvas\DeckCanvas.js
 * @author Lateral
 * @since  2023
 */

import { Button, Grid, Paper, Typography, Zoom, IconButton, useTheme, useMediaQuery, Stack } from '@mui/material'
import React, { forwardRef, useRef, useState, useEffect, useCallback } from 'react'
import DeckModel from './components/DeckModel/DeckModel'

import { useMaintenance } from 'hooks'
import { DeckMode } from 'common/deckMode'
import MaintenanceSelector from 'components/maintenanceSelector/MaintenanceSelector'
import { Undo, Redo } from '@mui/icons-material'
import { SelectionContextProvider, useSelectionContext } from 'components/selection/SelectionContext'
import { useCurrentDeckContext } from 'components/currentDeckContext/CurrentDeckContext'
import { useMultiSelect } from 'hooks/useMultiSelect'
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'
import BigNumber from 'bignumber.js'
import { ZoomControls } from '../../pages/deck/common/ZoomControls'
import { RevertButtonControl } from '../../pages/deck/common/RevertButtonControl'

import { DESKTOP_MIN_WIDTH } from 'common/config'
import { useKeyPress } from 'hooks/useKeyboardShortcut'
import { DeckMappToolTip } from 'components/tooltip/DeckMappToolTip'

// Utility function to check if a node is a child of the panels container
const hasPanelsContainerAsParent = (node) => {
  if (!node) {
    return false
  }
  if (node.id === 'panels-container-1') {
    return true
  }
  return hasPanelsContainerAsParent(node.parentNode)
}

// Wrapper for maintenance selector positioning - extracted outside to avoid recreation
const MaintainSelectorContainer = ({ isDesktop, children }) => {
  if (!isDesktop) {
    return children
  }

  return (
    <Stack display="flex" position="absolute" marginRight="1em" flexDirection="column" right="0" top="10%" gap=".5em">
      {children}
    </Stack>
  )
}

// Container component extracted outside to avoid recreation on every render
const Container = ({ onPanelClick, selectContainerRef, reset, wasMultiSelection, children }) => {
  const { setSelection } = useSelectionContext()

  return (
    <Paper
      ref={selectContainerRef}
      id="panels-container-1"
      onClick={(e) => {
        if (wasMultiSelection.current) {
          wasMultiSelection.current = false
          return
        }
        if (!e.target?.className?.includes?.('rosette') && hasPanelsContainerAsParent(e.target)) {
          reset()
          onPanelClick('cancel')
          setSelection(undefined)
        }
      }}
      elevation={8}
      sx={{
        marginTop: '0.1em',
        width: '100%',
        height: `100%`,
        backgroundColor: 'transparent',
        backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05));',
        display: 'flex',
        flexDirection: 'column',
        userSelect: 'none',
        position: 'relative'
      }}>
      {children}
    </Paper>
  )
}

export const MobileDeckCanvas = forwardRef(function DeckCanvas_(
  {
    data,
    maintenance,
    onPanelClick,
    onMaintenanceTypeClick,
    selected,
    materialSelected,
    mouseCanvasPosition,
    onWearSubmit,
    awaitingActionId,
    cancelAwaitingAction,
    mode,
    heatMapData,
    onUndo,
    isHistoryEmpty,
    onRedo,
    isHistoryAtEnd,
    saveDeckRevision,
    onSelectChange,
    type,
    inMaintenancePage
  },
  ref
) {
  /**
   * Generates 2D deck canvas
   *
   * @function
   * @param {object} data - Deck rivision object from database
   * @param {object} maintenance - Deck Revision Histories
   * @param {} onPanelClick - Action to click on a panel/sideliners. Same action applicable for panels and sideliners
   * @param {} onMaintenanceTypeClick - Action to click on Maintenance type in Maintenance mode
   * @param {object[]} selected - Array of selected panel objects
   * @param {number} materialSelected - Selected sideliner material number
   * @param {object} mouseCanvasPosition - x and y coordinates of pointer on canvas
   * @param {} onMaintenanceTypeClick - Action on changing panel/sideliner selection
   * @param {} onWearSubmit - Action on submiting wear in Maintenance mode
   * @param {number} awaitingActionId - id of await action for asynch operation
   * @param {number} cancelAwaitingAction - Id of await action when click cancel in maintenance mode
   * @param {string} mode - Current mode of deck layout
   * @returns {object} - React element of 3D deck canvas
   */
  const { actions } = useMaintenance()
  const theme = useTheme()
  const [isInserting, setIsInserting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const hasUsedKits = React.useMemo(
    () =>
      data?.Panels.some((p) => p.MaterialNumber !== undefined && p.MaterialNumber !== null) ||
      data?.SideLiners.some((s) => s.MaterialNumber !== undefined && s.MaterialNumber !== null),
    [data?.Panels, data?.SideLiners]
  )

  const initialScale = React.useMemo(
    () => Math.min(new BigNumber(1).dividedBy(data.Size.Rows).times(25).toNumber(), 1),
    [data.Size.Rows]
  )

  const canShowDeleteButton = mode === DeckMode.edit && saveDeckRevision && !hasUsedKits
  const [canShowMaintenanceSelector, setCanShowMaintenanceSelector] = useState(false)

  const [enablePanning, setEnablePanning] = useState(false)

  const onKeyPressed = useCallback((event) => {
    setEnablePanning(event.type === 'keydown' && event.target.tagName === 'BODY')
  }, [])

  useKeyPress(['Shift'], onKeyPressed)

  const {
    canMultiSelect,
    onPanelClick: onPanelClick_,
    onSinglePanelClick,
    reset
  } = useMultiSelect({
    onPanelClick,
    onSelectChange,
    awaitingActionId,
    type,
    mode,
    selected
  })

  // Update maintenance selector visibility based on mode and selection
  useEffect(() => {
    setCanShowMaintenanceSelector(
      mode === DeckMode.maintenance && !!mouseCanvasPosition && selected.length > 0 && type !== 'report'
    )
  }, [mode, mouseCanvasPosition, selected.length, type])

  const { panelQuery, setMaintenanceData, database } = useCurrentDeckContext()

  const onCancel = useCallback(() => {
    setIsInserting(false)
    setIsDeleting(false)
  }, [])

  const selectContainerRef = useRef(null)

  const isDesktop = useMediaQuery(DESKTOP_MIN_WIDTH)

  const wasMultiSelection = useRef(false)

  return (
    <SelectionContextProvider
      selectContainerRef={selectContainerRef}
      selected={selected}
      mode={mode}
      wasMultiSelection={wasMultiSelection}
      isEnabled={!enablePanning && !(mode === 'report' || mode === DeckMode.default)}
      onSinglePanelClick={onSinglePanelClick}
      onPanelClick={onPanelClick_}>
      <TransformWrapper
        centerOnInit
        limitToBounds={false}
        initialScale={initialScale}
        wheel={{
          disabled: !enablePanning
        }}
        panning={{
          disabled: !enablePanning
        }}
        minScale={initialScale}>
        <Container
          onPanelClick={onPanelClick}
          selectContainerRef={selectContainerRef}
          selected={selected}
          wasMultiSelection={wasMultiSelection}
          reset={reset}>
          <Grid
            container
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ userSelect: 'none', zIndex: 1000 }}
            padding=".4em">
            <Grid display="flex" flexDirection="column" alignItems="center">
              <Grid item>
                <RevertButtonControl />
                <DeckMappToolTip
                  title={
                    isDesktop ? (
                      <>
                        Zoom in or out = Shift + mouse scroll;
                        <br />
                        Pan = Hold Shift and left Click;
                        <br />
                        Multi Select = Left Mouse and drag selection box;
                        <br />
                        Remove from Selection = Hold Ctrl and left click individual panels;
                        <br />
                        Clear Selection = Click off panel layout;
                      </>
                    ) : (
                      'Push to select individual panels. There is no multi select in mobile version'
                    )
                  }
                />
              </Grid>
              {(onUndo || onRedo) && (
                <Grid item>
                  {onUndo && (
                    <IconButton
                      size="small"
                      onClick={onUndo}
                      disabled={isHistoryEmpty}
                      sx={{
                        marginRight: '1em',

                        '& *': {
                          opacity: isHistoryEmpty ? '50%' : '100%'
                        }
                      }}>
                      <Undo htmlColor={`${theme.palette.secondary.main}`} />
                    </IconButton>
                  )}
                  {onRedo && (
                    <IconButton
                      size="small"
                      onClick={onRedo}
                      disabled={isHistoryAtEnd}
                      sx={{
                        '& *': {
                          opacity: isHistoryAtEnd ? '50%' : '100%'
                        }
                      }}>
                      <Redo htmlColor={`${theme.palette.secondary.main}`} />
                    </IconButton>
                  )}
                </Grid>
              )}
            </Grid>

            {/* buttons for inserting/deleting columns/rows */}
            {canShowDeleteButton ? (
              <Grid item sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {isInserting || isDeleting ? (
                  <Button size="small" variant="outlined" onClick={() => onCancel()} sx={{ color: 'text.secondary' }}>
                    Cancel
                  </Button>
                ) : (
                  <>
                    <Button size="small" variant="outlined" onClick={() => setIsInserting(true)}>
                      Insert
                    </Button>
                    <Typography marginLeft="1em" marginRight="1em" component="p" color="text.secondary">
                      |
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setIsDeleting(true)}
                      sx={{ color: 'text.secondary' }}>
                      Delete
                    </Button>
                  </>
                )}
              </Grid>
            ) : null}
            <ZoomControls />
          </Grid>

          {(awaitingActionId === actions.Swap.id || awaitingActionId === actions.Relocate.id) && (
            <Zoom in>
              <Grid
                container
                sx={{
                  backgroundColor: 'black',
                  padding: '.6em',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  zIndex: '10000'
                }}>
                <Grid item>
                  <Typography>
                    {awaitingActionId === actions.Swap.id
                      ? 'Select panel to swap with'
                      : 'Where are you moving this panel to?'}
                  </Typography>
                </Grid>
                <Grid item>
                  <Button color="secondary" onClick={cancelAwaitingAction}>
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </Zoom>
          )}

          <Grid container sx={{ height: '100%' }}>
            <Grid item xs={12} sx={{ height: '100%', position: 'relative' }} ref={ref}>
              <TransformComponent
                contentStyle={{ display: 'block', width: '100%', height: '100%' }}
                wrapperStyle={{ width: '100%', height: '100%' }}>
                <DeckModel
                  data={data}
                  maintenance={maintenance}
                  onPanelClick={onSinglePanelClick}
                  selected={selected}
                  materialSelected={materialSelected}
                  mode={mode}
                  heatMapData={heatMapData}
                  onSelectChange={onSelectChange}
                  isMultiSelectEnabled={canMultiSelect}
                  type={type}
                  inMaintenancePage={inMaintenancePage}
                />
              </TransformComponent>
            </Grid>
            {canShowMaintenanceSelector && (
              <MaintainSelectorContainer isDesktop={isDesktop}>
                <MaintenanceSelector
                  isMobile={!isDesktop}
                  mouseCanvasPosition={mouseCanvasPosition}
                  onMaintenanceTypeClick={onMaintenanceTypeClick}
                  selected={selected}
                  maintenance={maintenance}
                  deckRevision={data}
                  onWearSubmit={onWearSubmit}
                  isPanelQuerySuccess={panelQuery.isSuccess}
                  isPanelDataQueryValid={panelQuery.data?.isValid}
                  setMaintenanceData={setMaintenanceData}
                  isPanelQueryFetching={panelQuery.isFetching}
                  database={database}
                />
              </MaintainSelectorContainer>
            )}
          </Grid>
        </Container>
      </TransformWrapper>
    </SelectionContextProvider>
  )
})

export default MobileDeckCanvas
